import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import ProtectedImage from "@/components/ProtectedImage";
import { apiClient, API_PREFIX } from "@/lib/api";

const API = API_PREFIX;
const DISPLAY_TIME_ZONE = "America/New_York";

// Chart options - metals including Rhodium
const METALS = [
  { id: "gold", name: "Gold" },
  { id: "silver", name: "Silver" },
  { id: "platinum", name: "Platinum" },
  { id: "palladium", name: "Palladium" },
  { id: "rhodium", name: "Rhodium" }
];
const THEMES = ["dark", "light"];
const LIGHT_ONLY_CHART_TYPES = new Set(["24h_compact"]);
const COMPACT_PREVIEW_WIDTH = 334;
const COMPACT_PREVIEW_HEIGHT = 256;
const DEFAULT_COMPACT_SAMPLING_PCT = 35;
const DEFAULT_COMPACT_PREVIEW_SCALE = 1;
const MIN_COMPACT_PREVIEW_SCALE = 0.75;
const MAX_COMPACT_PREVIEW_SCALE = 3;
const COMPACT_PREVIEW_SCALE_STEP = 0.25;
const COMPACT_SAMPLING_OPTIONS = [
  { value: 0, label: "0% drop" },
  { value: 15, label: "15% drop" },
  { value: 25, label: "25% drop" },
  { value: 35, label: "35% drop" },
  { value: 50, label: "50% drop" },
  { value: 65, label: "65% drop" }
];

// Units
const UNITS = [
  { id: "oz", name: "Troy Ounce", label: "/oz" },
  { id: "gram", name: "Gram", label: "/g" }
];

// Languages for chart labels
const LANGUAGES = [
  { id: "en", name: "English" },
  { id: "es", name: "Español" },
  { id: "fr", name: "Français" }
];

// Standard chart types (intraday)
const STANDARD_CHART_TYPES = [
  { id: "3day", name: "3-Day", category: "standard" },
  { id: "24h", name: "24h Live", category: "standard" },
  { id: "24h_simple", name: "24h Simple", category: "standard" },
  { id: "24h_compact", name: "24h Compact", category: "standard" },
  { id: "ny_hours", name: "NY Hours", category: "standard" }
];

// Historical chart types (longer periods)
const HISTORICAL_CHART_TYPES = [
  { id: "30d", name: "30 Day", category: "historical" },
  { id: "60d", name: "60 Day", category: "historical" },
  { id: "3m", name: "3 Month", category: "historical" },
  { id: "6m", name: "6 Month", category: "historical" },
  { id: "1y", name: "1 Year", category: "historical" },
  { id: "5y", name: "5 Year", category: "historical" },
  { id: "10y", name: "10 Year", category: "historical" },
  { id: "15y", name: "15 Year", category: "historical" },
  { id: "20y", name: "20 Year", category: "historical" },
  { id: "30y", name: "30 Year", category: "historical" },
  { id: "all", name: "All Time", category: "historical" }
];

// All chart types combined
const ALL_CHART_TYPES = [...STANDARD_CHART_TYPES, ...HISTORICAL_CHART_TYPES];
const STANDARD_CHART_TYPE_IDS = new Set(STANDARD_CHART_TYPES.map((type) => type.id));
const HISTORICAL_CHART_TYPE_IDS = new Set(HISTORICAL_CHART_TYPES.map((type) => type.id));
const ENABLED_HISTORICAL_CHART_TYPE_IDS = new Set(["30d", "60d", "3m"]);
const LIVE_MODE_STORAGE_KEY = "kcg.dashboard.live_mode";
const WEEKDAY_SHORT_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Supported currencies
const CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF " },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "MXN", name: "Mexican Peso", symbol: "MX$" },
  { code: "RUB", name: "Russian Ruble", symbol: "₽" },
  { code: "ZAR", name: "South African Rand", symbol: "R" }
];

// Helper to get currency symbol
const getCurrencySymbol = (code) => {
  const currency = CURRENCIES.find(c => c.code === code);
  return currency ? currency.symbol : "$";
};

const getSourceLabel = (source) => {
  if (source === "zeus") return "Zeus";
  if (source === "timescale") return "Timescale";
  if (source === "csv") return "CSV";
  return source || "Unknown";
};

const isLightOnlyChart = (chartType) => LIGHT_ONLY_CHART_TYPES.has(chartType);
const normalizeCompactSamplingPct = (_chartType, value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_COMPACT_SAMPLING_PCT;
  return Math.max(0, Math.min(80, Math.round(numeric)));
};
const clampCompactPreviewScale = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_COMPACT_PREVIEW_SCALE;
  return Math.max(
    MIN_COMPACT_PREVIEW_SCALE,
    Math.min(MAX_COMPACT_PREVIEW_SCALE, Math.round(numeric * 100) / 100)
  );
};
const isTemporarilyDisabledHistoricalChart = (chartType) =>
  HISTORICAL_CHART_TYPE_IDS.has(chartType) &&
  !ENABLED_HISTORICAL_CHART_TYPE_IDS.has(chartType);

const getDefaultLiveMode = () => {
  if (typeof window === "undefined") return true;
  const stored = window.localStorage.getItem(LIVE_MODE_STORAGE_KEY);
  if (stored === null) return true;
  return stored !== "false";
};

const formatDateTimeInputInTimeZone = (date, timeZone = DISPLAY_TIME_ZONE) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = {};
  formatter.formatToParts(date).forEach((part) => {
    if (part.type !== "literal") {
      parts[part.type] = part.value;
    }
  });
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const getDefaultReferenceTime = () => formatDateTimeInputInTimeZone(new Date());

const normalizeReferenceInputValue = (value) => {
  if (!value) return getDefaultReferenceTime();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value).slice(0, 16);
  }
  return formatDateTimeInputInTimeZone(parsed);
};

const formatEtTimestamp = (value) => {
  if (!value) return "Current ET";
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: DISPLAY_TIME_ZONE,
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(parsed);
  }
  return `${String(value).replace("T", " ").slice(0, 16)} ET`;
};

const describeReferenceWindow = (chartType) => {
  if (chartType === "3day") {
    return "Uses the selected ET day plus the two prior market days.";
  }
  if (chartType === "ny_hours") {
    return "Uses the selected ET session with recent close context.";
  }
  if (STANDARD_CHART_TYPE_IDS.has(chartType)) {
    return "Uses the selected ET day as the intraday as-of window.";
  }
  return "Uses the selected ET timestamp as the chart end time.";
};

const getWeekdayIndexInTimeZone = (date, timeZone = DISPLAY_TIME_ZONE) => {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  }).format(date);
  return WEEKDAY_SHORT_LABELS.indexOf(weekday);
};

const parseReferenceInputParts = (value) => {
  const normalized = normalizeReferenceInputValue(value);
  const [datePart, timePart = "00:00"] = normalized.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  return { year, month, day, hour, minute };
};

const buildReferenceInputValue = ({
  year,
  month,
  day,
  hour = 0,
  minute = 0,
}) =>
  `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

const buildLastWeekdayEndReferenceTime = (targetWeekday, nowEt) => {
  const { year, month, day } = parseReferenceInputParts(nowEt);
  const currentWeekday = getWeekdayIndexInTimeZone(new Date(), DISPLAY_TIME_ZONE);
  let daysBack = (currentWeekday - targetWeekday + 7) % 7;
  if (daysBack === 0) {
    daysBack = 7;
  }
  const baseDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  baseDate.setUTCDate(baseDate.getUTCDate() - daysBack);
  return buildReferenceInputValue({
    year: baseDate.getUTCFullYear(),
    month: baseDate.getUTCMonth() + 1,
    day: baseDate.getUTCDate(),
    hour: 23,
    minute: 59,
  });
};

const clampReferenceTimeForRequest = ({ value, nowEt }) => {
  const normalized = normalizeReferenceInputValue(value);
  const limit = normalizeReferenceInputValue(nowEt || getDefaultReferenceTime());
  return normalized > limit ? limit : normalized;
};

const REFERENCE_PRESET_OPTIONS = [
  { id: "now", label: "Now (ET)" },
  { id: "mon_end", label: "End of last Monday" },
  { id: "tue_end", label: "End of last Tuesday" },
  { id: "wed_end", label: "End of last Wednesday" },
  { id: "thu_end", label: "End of last Thursday" },
  { id: "fri_end", label: "End of last Friday" },
  { id: "sat_end", label: "End of last Saturday" },
  { id: "sun_end", label: "End of last Sunday" },
];

const appendCacheBust = (url, value) =>
  `${url}${url.includes("?") ? "&" : "?"}t=${value}`;

// Toggle component for quick switching
function ToggleSwitch({ options, value, onChange, label }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-zinc-400 text-sm">{label}</span>
      <div className="flex bg-zinc-800 rounded-lg p-1">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => onChange(option.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              value === option.id
                ? 'bg-zinc-100 text-zinc-900 shadow-sm'
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            {option.name}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [latestChart, setLatestChart] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageKey, setImageKey] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedMetal, setSelectedMetal] = useState("gold");
  const [selectedTheme, setSelectedTheme] = useState("dark");
  const [selectedChartType, setSelectedChartType] = useState("24h");
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [selectedUnit, setSelectedUnit] = useState("oz");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [selectedReferenceTime, setSelectedReferenceTime] = useState(() => getDefaultReferenceTime());
  const [currentEtNow, setCurrentEtNow] = useState(() => getDefaultReferenceTime());
  const [applySmoothing, setApplySmoothing] = useState(true);
  const [compactSamplingPct, setCompactSamplingPct] = useState(DEFAULT_COMPACT_SAMPLING_PCT);
  const [compactPreviewScale, setCompactPreviewScale] = useState(DEFAULT_COMPACT_PREVIEW_SCALE);
  const [dataSource, setDataSource] = useState("zeus");
  const [liveMode, setLiveMode] = useState(() => getDefaultLiveMode());
  const [csvFileName, setCsvFileName] = useState("");
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  const [csvFiles, setCsvFiles] = useState([]);
  const [isSwitching, setIsSwitching] = useState(false);
  const dropdownRef = useRef(null);
  const displayedFilename = latestChart?.filename || "";
  const isCompactPreview =
    displayedFilename.includes("24h_compact") ||
    (!latestChart && selectedChartType === "24h_compact");
  const compactPreviewPercent = Math.round(compactPreviewScale * 100);
  const compactPreviewWidth =
    COMPACT_PREVIEW_WIDTH * compactPreviewScale;

  const adjustCompactPreviewScale = (delta) => {
    setCompactPreviewScale((current) =>
      clampCompactPreviewScale(current + delta)
    );
  };

  const resolveApiError = (error) => {
    const data = error?.response?.data;
    if (data?.error) return data.error;
    if (data?.detail && typeof data.detail === "object") return data.detail;
    if (data?.detail && typeof data.detail === "string") {
      return { code: "CHART_GENERATION_FAILED", message: data.detail };
    }
    return null;
  };

  const syncReferenceTimeFromChart = (chart) => {
    if (chart?.reference_time_et) {
      setSelectedReferenceTime(normalizeReferenceInputValue(chart.reference_time_et));
    }
  };

  const applyReferenceTime = (value) => {
    setSelectedReferenceTime(
      clampReferenceTimeForRequest({
        value,
        nowEt: currentEtNow,
      })
    );
  };

  const buildChartPayload = ({
    metal = selectedMetal,
    theme = selectedTheme,
    chartType = selectedChartType,
    currency = selectedCurrency,
    unit = selectedUnit,
    language = selectedLanguage,
    smoothing = applySmoothing,
    compactSampling = compactSamplingPct,
    source = dataSource,
    referenceTime = selectedReferenceTime,
  } = {}) => {
    const normalizedTheme = isLightOnlyChart(chartType) ? "light" : theme;
    const normalizedCurrency = chartType === "ny_hours" ? "USD" : currency;
    const normalizedSmoothing = isLightOnlyChart(chartType) ? true : smoothing;
    const normalizedCompactSamplingPct = normalizeCompactSamplingPct(
      chartType,
      compactSampling
    );
    const normalizedReferenceTime = clampReferenceTimeForRequest({
      value: referenceTime,
      nowEt: currentEtNow,
    });
    const payload = {
      metal,
      size: "large",
      theme: normalizedTheme,
      chart_type: chartType,
      currency: normalizedCurrency,
      unit,
      language,
      apply_smoothing: normalizedSmoothing,
      data_source: source,
      reference_time_et: normalizedReferenceTime,
    };
    if (chartType === "24h_compact") {
      payload.compact_sampling_pct = normalizedCompactSamplingPct;
    }
    if (source === "csv" && csvFileName) {
      payload.csv_file = csvFileName;
    }
    return {
      payload,
      normalizedTheme,
      normalizedCurrency,
      normalizedSmoothing,
      normalizedCompactSamplingPct,
      normalizedReferenceTime,
    };
  };

  const buildLiveChartParams = ({
    metal = selectedMetal,
    theme = selectedTheme,
    chartType = selectedChartType,
    currency = selectedCurrency,
    unit = selectedUnit,
    language = selectedLanguage,
    smoothing = applySmoothing,
    compactSampling = compactSamplingPct,
    source = dataSource,
  } = {}) => {
    const normalizedTheme = isLightOnlyChart(chartType) ? "light" : theme;
    const normalizedCurrency = chartType === "ny_hours" ? "USD" : currency;
    const normalizedSmoothing = isLightOnlyChart(chartType) ? true : smoothing;
    const normalizedCompactSamplingPct = normalizeCompactSamplingPct(
      chartType,
      compactSampling
    );
    const params = {
      metal,
      size: "large",
      theme: normalizedTheme,
      chart_type: chartType,
      currency: normalizedCurrency,
      unit,
      language,
      apply_smoothing: normalizedSmoothing,
      data_source: source,
    };
    if (chartType === "24h_compact") {
      params.compact_sampling_pct = normalizedCompactSamplingPct;
    }
    if (source === "csv" && csvFileName) {
      params.csv_file = csvFileName;
    }
    return {
      params,
      normalizedTheme,
      normalizedCurrency,
      normalizedSmoothing,
      normalizedCompactSamplingPct,
    };
  };

  const applyChartResponse = (chart) => {
    setLatestChart(chart);
    syncReferenceTimeFromChart(chart);
    setImageKey(Date.now());
  };

  const fetchLatestLiveChartVariant = async (
    options = {},
    { fallbackToLatest = false } = {}
  ) => {
    const {
      params,
      normalizedTheme,
      normalizedCurrency,
      normalizedSmoothing,
      normalizedCompactSamplingPct,
    } = buildLiveChartParams(options);
    try {
      const response = await apiClient.get(`${API}/charts/latest`, {
        params: {
          ...params,
          prefer_storage: true,
        },
      });
      return {
        response,
        normalizedTheme,
        normalizedCurrency,
        normalizedSmoothing,
        normalizedCompactSamplingPct,
      };
    } catch (error) {
      if (fallbackToLatest && error?.response?.status === 404) {
        const response = await apiClient.get(`${API}/charts/latest`, {
          params: { prefer_storage: true },
        });
        return {
          response,
          normalizedTheme,
          normalizedCurrency,
          normalizedSmoothing,
          normalizedCompactSamplingPct,
        };
      }
      throw error;
    }
  };

  const generateLiveChartVariant = async (options = {}) => {
    const {
      payload,
      normalizedTheme,
      normalizedCurrency,
      normalizedSmoothing,
      normalizedCompactSamplingPct,
      normalizedReferenceTime,
    } = buildChartPayload({
      ...options,
      referenceTime: currentEtNow,
    });
    const response = await apiClient.post(`${API}/charts`, payload);
    return {
      response,
      normalizedTheme,
      normalizedCurrency,
      normalizedSmoothing,
      normalizedCompactSamplingPct,
      normalizedReferenceTime,
    };
  };

  const loadOrGenerateLiveChartVariant = async (
    options = {},
    { fallbackToLatest = false, generateOnMissing = false } = {}
  ) => {
    try {
      return await fetchLatestLiveChartVariant(options, { fallbackToLatest });
    } catch (error) {
      if (generateOnMissing && error?.response?.status === 404) {
        return await generateLiveChartVariant(options);
      }
      throw error;
    }
  };

  useEffect(() => {
    const refreshNow = () => {
      setCurrentEtNow(getDefaultReferenceTime());
    };
    refreshNow();
    const intervalId = window.setInterval(refreshNow, 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const clamped = clampReferenceTimeForRequest({
      value: selectedReferenceTime,
      nowEt: currentEtNow,
    });
    if (clamped !== selectedReferenceTime) {
      setSelectedReferenceTime(clamped);
    }
  }, [currentEtNow, selectedChartType, dataSource, selectedReferenceTime]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      LIVE_MODE_STORAGE_KEY,
      liveMode ? "true" : "false"
    );
  }, [liveMode]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const statusPromise = apiClient
          .get(`${API}/status`)
          .catch(() => ({ data: null }));

        let chartRes = null;
        if (liveMode) {
          chartRes = await fetchLatestLiveChartVariant({}, { fallbackToLatest: true })
            .then((result) => result.response)
            .catch(() => null);
        } else {
          chartRes = await apiClient
            .get(`${API}/charts/latest`)
            .catch(() => ({ data: null }));
        }
        if (chartRes?.data) {
          applyChartResponse(chartRes.data);
        }
        setLoading(false);

        const statusRes = await statusPromise;
        if (statusRes?.data) setSystemStatus(statusRes.data);
      } catch (error) {
        console.error("Failed to load data:", error);
        setLoading(false);
      }
    };
    loadData();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadCsvFiles = async () => {
    try {
      const response = await apiClient.get(`${API}/data-sources/csv`);
      setCsvFiles(response.data.files || []);
    } catch (error) {
      // ignore
    }
  };

  useEffect(() => {
    if (dataSource === "csv") {
      loadCsvFiles();
    }
  }, [dataSource]);

  const handleLiveModeChange = async (enabled) => {
    setLiveMode(enabled);
    if (!enabled) {
      return;
    }
    if (dataSource === "csv" && !csvFileName) {
      return;
    }
    setIsSwitching(true);
    try {
      const {
        response,
        normalizedTheme,
        normalizedCurrency,
        normalizedSmoothing,
        normalizedCompactSamplingPct,
        normalizedReferenceTime,
      } = await loadOrGenerateLiveChartVariant({
        metal: selectedMetal,
        theme: selectedTheme,
        chartType: selectedChartType,
        currency: selectedCurrency,
        unit: selectedUnit,
        language: selectedLanguage,
        smoothing: applySmoothing,
        compactSampling: compactSamplingPct,
        source: dataSource,
      }, { generateOnMissing: true });
      setSelectedTheme(normalizedTheme);
      setSelectedCurrency(normalizedCurrency);
      setApplySmoothing(normalizedSmoothing);
      setCompactSamplingPct(normalizedCompactSamplingPct);
      if (normalizedReferenceTime && normalizedReferenceTime !== selectedReferenceTime) {
        setSelectedReferenceTime(normalizedReferenceTime);
      }
      applyChartResponse(response.data);
    } catch (error) {
      const apiError = resolveApiError(error);
      toast.error(apiError?.message || "Failed to load latest live chart");
    } finally {
      setIsSwitching(false);
    }
  };

  const handleReferencePresetSelect = (presetId) => {
    if (!presetId) return;
    if (presetId === "now") {
      applyReferenceTime(currentEtNow);
      return;
    }
    const weekdayMap = {
      mon_end: 1,
      tue_end: 2,
      wed_end: 3,
      thu_end: 4,
      fri_end: 5,
      sat_end: 6,
      sun_end: 0,
    };
    const targetWeekday = weekdayMap[presetId];
    if (targetWeekday === undefined) {
      return;
    }
    applyReferenceTime(
      buildLastWeekdayEndReferenceTime(targetWeekday, currentEtNow)
    );
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;
    setIsUploadingCsv(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiClient.post(`${API}/data-sources/csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setCsvFileName(response.data.filename || "");
      setDataSource("csv");
      await loadCsvFiles();
      toast.success("CSV uploaded. Using CSV as data source.");
    } catch (error) {
      toast.error("CSV upload failed");
    } finally {
      setIsUploadingCsv(false);
    }
  };

  const handleGenerateChart = async () => {
    if (dataSource === "csv" && !csvFileName) {
      toast.error("Please upload a CSV file first.");
      return;
    }
    setIsGenerating(true);
    setShowDropdown(false);
    try {
      const {
        payload,
        normalizedTheme,
        normalizedCurrency,
        normalizedSmoothing,
        normalizedCompactSamplingPct,
        normalizedReferenceTime,
      } = buildChartPayload();
      if (normalizedTheme !== selectedTheme) setSelectedTheme(normalizedTheme);
      if (normalizedCurrency !== selectedCurrency) setSelectedCurrency(normalizedCurrency);
      if (normalizedSmoothing !== applySmoothing) setApplySmoothing(normalizedSmoothing);
      if (normalizedCompactSamplingPct !== compactSamplingPct) {
        setCompactSamplingPct(normalizedCompactSamplingPct);
      }
      if (normalizedReferenceTime !== selectedReferenceTime) {
        setSelectedReferenceTime(normalizedReferenceTime);
      }
      const response = await apiClient.post(`${API}/charts`, payload);
      const metalName = METALS.find(m => m.id === selectedMetal)?.name || selectedMetal;
      const unitLabel = UNITS.find(u => u.id === selectedUnit)?.label || "/oz";
      toast.success(`${metalName} ${selectedChartType} ${normalizedCurrency}${unitLabel} chart generated for ${normalizedReferenceTime.replace("T", " ")} ET.`);
      applyChartResponse(response.data);
      apiClient.get(`${API}/status`)
        .then((statusRes) => setSystemStatus(statusRes.data))
        .catch(() => {});
    } catch (error) {
      const apiError = resolveApiError(error);
      if (apiError?.code && (apiError.code.startsWith("CSV_") || apiError.code === "TIMESCALE_DATA_UNAVAILABLE" || apiError.code === "DATA_FETCH_FAILED")) {
        navigate("/error", { state: { error: apiError } });
        return;
      }
      toast.error(apiError?.message || "Chart generation failed");
    }
    setIsGenerating(false);
  };

  // Quick toggle handler - either loads the latest live variant or falls back
  // to the legacy fetch/generate flow when live mode is disabled.
  const handleQuickToggle = async (type, value) => {
    const next = {
      theme: type === "theme" ? value : selectedTheme,
      chartType: type === "chartType" ? value : selectedChartType,
      currency: type === "currency" ? value : selectedCurrency,
      unit: type === "unit" ? value : selectedUnit,
      language: type === "language" ? value : selectedLanguage,
      metal: type === "metal" ? value : selectedMetal,
      smoothing: type === "smoothing" ? value : applySmoothing,
      compactSampling: type === "compactSamplingPct" ? Number(value) : compactSamplingPct,
      source: type === "source" ? value : dataSource,
    };
    if (type === "theme" && isLightOnlyChart(next.chartType) && value !== "light") {
      return;
    }
    if (next.chartType === "ny_hours") {
      next.currency = "USD";
    }
    if (isLightOnlyChart(next.chartType)) {
      next.theme = "light";
      next.smoothing = true;
    }
    if (isTemporarilyDisabledHistoricalChart(next.chartType)) {
      return;
    }
    if (next.source === "csv" && !csvFileName) {
      toast.error("Please upload a CSV file first.");
      return;
    }

    const {
      normalizedTheme: liveNormalizedTheme,
      normalizedCurrency: liveNormalizedCurrency,
      normalizedSmoothing: liveNormalizedSmoothing,
      normalizedCompactSamplingPct: liveNormalizedCompactSamplingPct,
    } = buildLiveChartParams({
      metal: next.metal,
      theme: next.theme,
      chartType: next.chartType,
      currency: next.currency,
      unit: next.unit,
      language: next.language,
      smoothing: next.smoothing,
      compactSampling: next.compactSampling,
      source: next.source,
    });

    setIsSwitching(true);
    try {
      let response;
      let normalizedTheme = liveNormalizedTheme;
      let normalizedCurrency = liveNormalizedCurrency;
      let normalizedSmoothing = liveNormalizedSmoothing;
      let normalizedCompactSamplingPct = liveNormalizedCompactSamplingPct;
      let normalizedReferenceTime = null;

      if (liveMode) {
        ({
          response,
          normalizedTheme,
          normalizedCurrency,
          normalizedSmoothing,
          normalizedCompactSamplingPct,
          normalizedReferenceTime,
        } = await loadOrGenerateLiveChartVariant({
          metal: next.metal,
          theme: next.theme,
          chartType: next.chartType,
          currency: next.currency,
          unit: next.unit,
          language: next.language,
          smoothing: next.smoothing,
          compactSampling: next.compactSampling,
          source: next.source,
        }, { generateOnMissing: true }));
      } else {
        const {
          payload,
          normalizedTheme: nextTheme,
          normalizedCurrency: nextCurrency,
          normalizedSmoothing: nextSmoothing,
          normalizedCompactSamplingPct: nextCompactSamplingPct,
          normalizedReferenceTime,
        } = buildChartPayload({
          metal: next.metal,
          theme: next.theme,
          chartType: next.chartType,
          currency: next.currency,
          unit: next.unit,
          language: next.language,
          smoothing: next.smoothing,
          compactSampling: next.compactSampling,
          source: next.source,
        });
        normalizedTheme = nextTheme;
        normalizedCurrency = nextCurrency;
        normalizedSmoothing = nextSmoothing;
        normalizedCompactSamplingPct = nextCompactSamplingPct;
        if (normalizedReferenceTime !== selectedReferenceTime) {
          setSelectedReferenceTime(normalizedReferenceTime);
        }
        try {
          response = await apiClient.get(`${API}/charts`, {
            params: {
              ...payload,
            },
          });
        } catch (error) {
          if (error?.response?.status !== 404) {
            throw error;
          }
          response = await apiClient.post(`${API}/charts`, payload);
        }
      }
      setSelectedTheme(normalizedTheme);
      setSelectedChartType(next.chartType);
      setSelectedCurrency(normalizedCurrency);
      setSelectedUnit(next.unit);
      setSelectedLanguage(next.language);
      setSelectedMetal(next.metal);
      setApplySmoothing(normalizedSmoothing);
      setCompactSamplingPct(normalizedCompactSamplingPct);
      setDataSource(next.source);
      if (normalizedReferenceTime && normalizedReferenceTime !== selectedReferenceTime) {
        setSelectedReferenceTime(normalizedReferenceTime);
      }
      applyChartResponse(response.data);
    } catch (error) {
      const apiError = resolveApiError(error);
      toast.error(
        apiError?.message ||
          (liveMode ? "Failed to load latest live chart" : "Failed to load chart variant")
      );
      if (apiError?.code && (apiError.code.startsWith("CSV_") || apiError.code === "TIMESCALE_DATA_UNAVAILABLE" || apiError.code === "DATA_FETCH_FAILED")) {
        navigate("/error", { state: { error: apiError } });
      }
    } finally {
      setIsSwitching(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-zinc-600 border-t-zinc-100 rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#09090B] p-4 md:p-6">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-100">Kitco Chart Generator</h1>
          <p className="text-zinc-400 text-sm mt-1">KCG - Metal Price Charts</p>
        </div>
        <div className="relative" ref={dropdownRef}>
          <button 
            data-testid="generate-chart-btn"
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={isGenerating}
            className="bg-zinc-100 text-zinc-900 hover:bg-zinc-200 font-medium rounded-md px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"
          >
            {isGenerating ? "Generating..." : "Generate Chart"}
            <svg className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-80 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 p-4 max-h-[80vh] overflow-y-auto">
              {/* Metal Selection - Dropdown */}
              <div className="mb-4">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Metal</label>
                <select
                  value={selectedMetal}
                  onChange={(e) => setSelectedMetal(e.target.value)}
                  className="w-full bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {METALS.map((metal) => (
                    <option key={metal.id} value={metal.id}>
                      {metal.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">As-Of Time</label>
                <input
                  type="datetime-local"
                  value={selectedReferenceTime}
                  max={currentEtNow}
                  onChange={(e) => applyReferenceTime(e.target.value)}
                  className="w-full bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleReferencePresetSelect("now")}
                    className="px-3 py-1.5 text-xs rounded-md bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                  >
                    Now (ET)
                  </button>
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      handleReferencePresetSelect(e.target.value);
                      e.target.value = "";
                    }}
                    className="flex-1 min-w-[180px] bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Quick ET presets</option>
                    {REFERENCE_PRESET_OPTIONS.filter((option) => option.id !== "now").map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">
                  Interpreted in America/New_York. {describeReferenceWindow(selectedChartType)}
                </p>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Future ET selections are clamped to the current New York time.
                </p>
              </div>
              
              {/* Theme Selection */}
              <div className="mb-4">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Theme</label>
                <div className="grid grid-cols-2 gap-2">
                  {THEMES.map((theme) => (
                    <button
                      key={theme}
                      onClick={() => setSelectedTheme(theme)}
                      disabled={isLightOnlyChart(selectedChartType) && theme !== "light"}
                      className={`px-3 py-2 text-sm rounded-md transition-colors capitalize ${
                        selectedTheme === theme 
                          ? 'bg-zinc-100 text-zinc-900 font-medium' 
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      } ${(isLightOnlyChart(selectedChartType) && theme !== "light") ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
                {isLightOnlyChart(selectedChartType) && (
                  <p className="text-[11px] text-zinc-500 mt-2">24h Compact is light theme only.</p>
                )}
              </div>
              
              {/* Chart Type Selection */}
              <div className="mb-4">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Chart Type</label>
                
                {/* Standard Types */}
                <p className="text-xs text-zinc-500 mb-1">Intraday</p>
                <div className="grid grid-cols-2 gap-1 mb-2">
                  {STANDARD_CHART_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => {
                        setSelectedChartType(type.id);
                        if (type.id === "ny_hours") setSelectedCurrency("USD");
                        if (isLightOnlyChart(type.id)) {
                          setSelectedTheme("light");
                          setApplySmoothing(true);
                        }
                      }}
                      className={`px-2 py-1.5 text-xs rounded-md transition-colors text-left ${
                        selectedChartType === type.id 
                          ? 'bg-zinc-100 text-zinc-900 font-medium' 
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      {type.name}
                    </button>
                  ))}
                </div>
                
                {/* Historical Types */}
                <p className="text-xs text-zinc-500 mb-1">Historical</p>
                <div className="grid grid-cols-4 gap-1">
                  {HISTORICAL_CHART_TYPES.map((type) => {
                    const disabled = isTemporarilyDisabledHistoricalChart(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => !disabled && setSelectedChartType(type.id)}
                        disabled={disabled}
                        className={`px-2 py-1.5 text-xs rounded-md transition-colors text-center ${
                          selectedChartType === type.id 
                            ? 'bg-emerald-500 text-white font-medium' 
                            : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                        } ${disabled ? 'opacity-40 cursor-not-allowed hover:bg-zinc-800' : ''}`}
                      >
                        {type.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">
                  6 Month and longer historical charts are temporarily disabled until the backfill completes.
                </p>
              </div>

              {/* Data Source Selection */}
              <div className="mb-4">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Data Source</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setDataSource("timescale")}
                    className={`min-h-[44px] px-3 py-2 text-sm rounded-md transition-colors whitespace-normal break-words leading-tight ${
                      dataSource === "timescale"
                        ? 'bg-zinc-100 text-zinc-900 font-medium'
                        : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                    }`}
                  >
                    TimescaleDB
                  </button>
                  <button
                    onClick={() => setDataSource("csv")}
                    className={`min-h-[44px] px-3 py-2 text-sm rounded-md transition-colors whitespace-normal break-words leading-tight ${
                      dataSource === "csv"
                        ? 'bg-emerald-500/20 text-emerald-300 font-medium border border-emerald-500/30'
                        : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                    }`}
                  >
                    CSV Upload
                  </button>
                  <button
                    onClick={() => setDataSource("zeus")}
                    className={`col-span-2 min-h-[44px] px-3 py-2 text-sm rounded-md transition-colors whitespace-normal break-words leading-tight ${
                      dataSource === "zeus"
                        ? 'bg-zinc-100 text-zinc-900 font-medium'
                        : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                    }`}
                  >
                    Zeus
                  </button>
                </div>
                {dataSource === "csv" && (
                  <div className="mt-3">
                    <label className="text-xs text-zinc-400 block mb-1">Upload CSV (precious metals)</label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleCsvUpload(e.target.files?.[0])}
                      className="w-full text-xs text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-zinc-800 file:text-zinc-300 hover:file:bg-zinc-700"
                    />
                    <div className="mt-3">
                      <label className="text-xs text-zinc-400 block mb-1">Use Existing CSV</label>
                      <select
                        value={csvFileName}
                        onChange={(e) => setCsvFileName(e.target.value)}
                        className="w-full bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs"
                      >
                        <option value="">Select a file</option>
                        {csvFiles.map((file) => (
                          <option key={file.filename} value={file.filename}>
                            {file.filename}
                          </option>
                        ))}
                      </select>
                    </div>
                    {isUploadingCsv && (
                      <p className="text-xs text-zinc-500 mt-1">Uploading...</p>
                    )}
                    {!isUploadingCsv && csvFileName && (
                      <p className="text-xs text-emerald-400 mt-1 truncate">Using: {csvFileName}</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Currency Selection - Hidden for NY Hours (USD only) */}
              {selectedChartType !== "ny_hours" && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Currency</label>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    className="w-full bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {CURRENCIES.map((curr) => (
                      <option key={curr.code} value={curr.code}>
                        {curr.code} - {curr.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {selectedChartType === "ny_hours" && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Currency</label>
                  <div className="w-full bg-zinc-800/50 text-zinc-500 border border-zinc-700 rounded-md px-3 py-2 text-sm">
                    USD only (NY trading hours)
                  </div>
                </div>
              )}
              
              {/* Unit Selection */}
              <div className="mb-4">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Unit</label>
                <div className="grid grid-cols-2 gap-2">
                  {UNITS.map((unit) => (
                    <button
                      key={unit.id}
                      onClick={() => setSelectedUnit(unit.id)}
                      className={`px-3 py-2 text-sm rounded-md transition-colors ${
                        selectedUnit === unit.id 
                          ? 'bg-amber-500/20 text-amber-300 font-medium border border-amber-500/30' 
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      {unit.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Smoothing Selection */}
              {!isLightOnlyChart(selectedChartType) && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Smoothing</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setApplySmoothing(true)}
                      className={`px-3 py-2 text-sm rounded-md transition-colors ${
                        applySmoothing
                          ? 'bg-emerald-500/20 text-emerald-300 font-medium border border-emerald-500/30'
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      On
                    </button>
                    <button
                      onClick={() => setApplySmoothing(false)}
                      className={`px-3 py-2 text-sm rounded-md transition-colors ${
                        !applySmoothing
                          ? 'bg-zinc-100 text-zinc-900 font-medium'
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>
              )}

              {isLightOnlyChart(selectedChartType) && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Smoothing</label>
                  <div className="w-full bg-zinc-800/50 text-zinc-500 border border-zinc-700 rounded-md px-3 py-2 text-sm">
                    Compact uses automatic render-path smoothing
                  </div>
                </div>
              )}

              {selectedChartType === "24h_compact" && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Sample Drop</label>
                  <select
                    value={compactSamplingPct}
                    onChange={(e) => setCompactSamplingPct(Number(e.target.value))}
                    className="w-full bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {COMPACT_SAMPLING_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                        {option.value === DEFAULT_COMPACT_SAMPLING_PCT ? " (Recommended)" : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-zinc-500 mt-2">
                    Higher drop simplifies the compact line before curve smoothing.
                  </p>
                </div>
              )}
              
              {/* Language Selection (for chart labels) */}
              <div className="mb-4">
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">Chart Language</label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="w-full bg-zinc-800 text-zinc-300 border border-zinc-700 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.id} value={lang.id}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Generate Button */}
              <button
                onClick={handleGenerateChart}
                disabled={isGenerating}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md px-4 py-2.5 transition-colors disabled:opacity-50"
              >
                {isGenerating ? "Generating..." : "Generate"}
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div data-testid="chart-display-card" className="col-span-1 md:col-span-8 lg:col-span-9 bg-[#18181B] border border-zinc-800 rounded-md p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-xl text-zinc-100 font-semibold">Latest Chart</h2>
            {isCompactPreview && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Preview Zoom
                </span>
                <button
                  type="button"
                  onClick={() => adjustCompactPreviewScale(-COMPACT_PREVIEW_SCALE_STEP)}
                  disabled={compactPreviewScale <= MIN_COMPACT_PREVIEW_SCALE}
                  className="h-8 w-8 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-200 transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  -
                </button>
                <span className="min-w-[4rem] text-center text-sm text-zinc-300">
                  {compactPreviewPercent}%
                </span>
                <button
                  type="button"
                  onClick={() => adjustCompactPreviewScale(COMPACT_PREVIEW_SCALE_STEP)}
                  disabled={compactPreviewScale >= MAX_COMPACT_PREVIEW_SCALE}
                  className="h-8 w-8 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-200 transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  +
                </button>
              </div>
            )}
          </div>
          <div
            className={`relative w-full bg-black border border-zinc-800 flex items-center justify-center rounded-md ${
              isCompactPreview ? "overflow-auto" : "overflow-hidden"
            } ${
              isCompactPreview ? "min-h-0 p-4 md:p-6" : "min-h-[500px]"
            }`}
          >
            {isSwitching && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                <div className="animate-spin h-8 w-8 border-2 border-zinc-600 border-t-zinc-100 rounded-full"></div>
              </div>
            )}
            {latestChart ? (
              <div
                className={isCompactPreview ? "flex-shrink-0" : "w-full"}
                style={
                  isCompactPreview
                    ? {
                        width:
                          compactPreviewScale === DEFAULT_COMPACT_PREVIEW_SCALE
                            ? "100%"
                            : `${compactPreviewWidth}px`,
                        maxWidth:
                          compactPreviewScale === DEFAULT_COMPACT_PREVIEW_SCALE
                            ? `${COMPACT_PREVIEW_WIDTH}px`
                            : "none",
                        aspectRatio: `${COMPACT_PREVIEW_WIDTH} / ${COMPACT_PREVIEW_HEIGHT}`,
                      }
                    : undefined
                }
              >
                <ProtectedImage
                  data-testid="chart-image"
                  src={appendCacheBust(latestChart.image_url, imageKey)}
                  alt="Metal Spot Chart"
                  className={`block object-contain ${
                    isCompactPreview ? "w-full h-full" : "w-full h-auto"
                  }`}
                  errorFallback={
                    <div
                      className={`text-zinc-600 font-mono text-sm text-center flex items-center justify-center ${
                        isCompactPreview ? "w-full h-full" : "py-20"
                      }`}
                    >
                      <p>Unable to load chart preview</p>
                    </div>
                  }
                />
              </div>
            ) : (
              <div className="text-zinc-600 font-mono text-sm text-center py-20">
                <p>No charts generated yet</p>
                <p className="text-xs text-zinc-700 mt-2">Click "Generate Chart" to create one</p>
              </div>
            )}
          </div>
          
          {/* Quick Toggle Controls */}
          {latestChart && (
            <div data-testid="chart-toggles" className="mt-4 pt-4 border-t border-zinc-800 space-y-3">
              {/* First row: Metal + Intraday charts + Theme */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Metal Dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 text-sm">Metal</span>
                  <select
                    value={selectedMetal}
                    onChange={(e) => handleQuickToggle('metal', e.target.value)}
                    className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    {METALS.map((metal) => (
                      <option key={metal.id} value={metal.id}>
                        {metal.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <ToggleSwitch
                  label="Intraday"
                  options={[
                    { id: "24h", name: "24h" },
                    { id: "24h_simple", name: "Simple" },
                    { id: "24h_compact", name: "Compact" },
                    { id: "ny_hours", name: "NY" },
                    { id: "3day", name: "3-Day" }
                  ]}
                  value={STANDARD_CHART_TYPES.some(t => t.id === selectedChartType) ? selectedChartType : ""}
                  onChange={(val) => handleQuickToggle('chartType', val)}
                />
                <ToggleSwitch
                  label="Theme"
                  options={isLightOnlyChart(selectedChartType)
                    ? [{ id: "light", name: "Light" }]
                    : [
                        { id: "dark", name: "Dark" },
                        { id: "light", name: "Light" }
                      ]}
                  value={selectedTheme}
                  onChange={(val) => handleQuickToggle('theme', val)}
                />
              </div>
              
              {/* Second row: Currency + Unit + Language */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Currency Dropdown - Hidden for NY Hours */}
                {selectedChartType !== "ny_hours" && (
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400 text-sm">Currency</span>
                    <select
                      value={selectedCurrency}
                      onChange={(e) => handleQuickToggle('currency', e.target.value)}
                      className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      {CURRENCIES.map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.code}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Unit Toggle */}
                <ToggleSwitch
                  label="Unit"
                  options={[
                    { id: "oz", name: "oz" },
                    { id: "gram", name: "gram" }
                  ]}
                  value={selectedUnit}
                  onChange={(val) => handleQuickToggle('unit', val)}
                />

                {/* Smoothing Toggle */}
                {!isLightOnlyChart(selectedChartType) && (
                  <ToggleSwitch
                    label="Smoothing"
                    options={[
                      { id: true, name: "On" },
                      { id: false, name: "Off" }
                    ]}
                    value={applySmoothing}
                    onChange={(val) => handleQuickToggle('smoothing', val)}
                  />
                )}

                {selectedChartType === "24h_compact" && (
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400 text-sm">Drop</span>
                    <select
                      value={compactSamplingPct}
                      onChange={(e) => handleQuickToggle('compactSamplingPct', Number(e.target.value))}
                      className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      {COMPACT_SAMPLING_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.value}%
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Source Toggle */}
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 text-sm">Source</span>
                  <select
                    value={dataSource}
                    onChange={(e) => handleQuickToggle('source', e.target.value)}
                    className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    <option value="zeus">Zeus</option>
                    <option value="timescale">Timescale</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>

                {/* Language Dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 text-sm">Lang</span>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => handleQuickToggle('language', e.target.value)}
                    className="bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    {LANGUAGES.map((lang) => (
                      <option key={lang.id} value={lang.id}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>

                <ToggleSwitch
                  label="Live Mode"
                  options={[
                    { id: true, name: "On" },
                    { id: false, name: "Off" }
                  ]}
                  value={liveMode}
                  onChange={(val) => handleLiveModeChange(val)}
                />
              </div>
              
              {/* Third row: Historical charts */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-zinc-400 text-sm">Historical</span>
                <div className="flex flex-wrap bg-zinc-800 rounded-lg p-1 gap-1">
                  {HISTORICAL_CHART_TYPES.map((type) => {
                    const disabled = isTemporarilyDisabledHistoricalChart(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => !disabled && handleQuickToggle('chartType', type.id)}
                        disabled={disabled}
                        className={`px-2 py-1 text-xs font-medium rounded-md transition-all ${
                          selectedChartType === type.id
                            ? 'bg-emerald-500 text-white shadow-sm'
                            : 'text-zinc-400 hover:text-zinc-200'
                        } ${disabled ? 'opacity-40 cursor-not-allowed hover:text-zinc-400' : ''}`}
                      >
                        {type.name}
                      </button>
                    );
                  })}
                </div>
                <span className="text-zinc-600 text-xs">Up to 3 Month only for now</span>
              </div>
              
              {/* Status indicator */}
              <div className="flex justify-end">
                <span className="text-zinc-600 text-xs">
                  {METALS.find(m => m.id === selectedMetal)?.name || selectedMetal} • {selectedChartType} • {selectedTheme} • {selectedUnit}{selectedChartType !== "ny_hours" ? ` • ${selectedCurrency}` : ""}{selectedLanguage !== "en" ? ` • ${selectedLanguage}` : ""}{!isLightOnlyChart(selectedChartType) ? ` • ${applySmoothing ? "Smoothing On" : "Smoothing Off"}` : ""}{selectedChartType === "24h_compact" ? ` • Drop ${compactSamplingPct}%` : ""} • {getSourceLabel(dataSource)} • Live Mode {liveMode ? "On" : "Off"} • As of {selectedReferenceTime.replace("T", " ")} ET
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="col-span-1 md:col-span-4 lg:col-span-3 flex flex-col gap-4">
          <div data-testid="system-status-card" className="bg-[#18181B] border border-zinc-800 rounded-md p-4">
            <h3 className="text-lg text-zinc-100 font-semibold mb-3">System Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Status</span>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                  <span className="text-zinc-100 font-mono text-sm uppercase">{systemStatus?.status || 'Unknown'}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Charts Generated</span>
                <span className="text-zinc-100 font-mono text-lg">{systemStatus?.charts_generated || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">Uptime</span>
                <span className="text-zinc-100 font-mono text-sm">{systemStatus?.uptime || '-'}</span>
              </div>
            </div>
          </div>

          {latestChart?.stats && (
            <div data-testid="price-stats-card" className="bg-[#18181B] border border-zinc-800 rounded-md p-4">
              <h3 className="text-lg text-zinc-100 font-semibold mb-3">{latestChart.metal} Price</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Current</span>
                  <span className="text-zinc-100 font-mono text-xl">{getCurrencySymbol(selectedCurrency)}{latestChart.stats.current_price?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Change</span>
                  <span className={`font-mono text-sm ${latestChart.stats.change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {latestChart.stats.change >= 0 ? '+' : ''}{latestChart.stats.change?.toFixed(2)} ({latestChart.stats.change_percent >= 0 ? '+' : ''}{latestChart.stats.change_percent?.toFixed(2)}%)
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">High / Low</span>
                  <span className="text-zinc-100 font-mono text-sm">{getCurrencySymbol(selectedCurrency)}{latestChart.stats.high?.toFixed(2)} / {getCurrencySymbol(selectedCurrency)}{latestChart.stats.low?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <div data-testid="last-generation-card" className="bg-[#18181B] border border-zinc-800 rounded-md p-4">
            <h3 className="text-lg text-zinc-100 font-semibold mb-3">Last Generation</h3>
            {latestChart ? (
              <div className="space-y-2">
                <span className="text-emerald-500 text-xs border border-emerald-500/30 rounded px-2 py-0.5">Success</span>
                <p className="text-zinc-100 font-mono text-xs">{new Date(latestChart.generated_at).toLocaleString()}</p>
                {latestChart.reference_time_et && (
                  <p className="text-zinc-300 text-xs">As of {formatEtTimestamp(latestChart.reference_time_et)} ET</p>
                )}
                {latestChart.query_window && (
                  <p className="text-zinc-500 text-xs">
                    Query window: {formatEtTimestamp(latestChart.query_window.start_et)} ET to {formatEtTimestamp(latestChart.query_window.end_et)} ET
                  </p>
                )}
                {(latestChart.requested_data_source || latestChart.resolved_data_source) && (
                  <p className="text-zinc-500 text-xs">
                    Source: {getSourceLabel(latestChart.requested_data_source)} requested
                    {latestChart.resolved_data_source ? `, ${getSourceLabel(latestChart.resolved_data_source)} resolved` : ""}
                    {latestChart.fallback_used ? " (Timescale fallback used)" : ""}
                  </p>
                )}
                <p className="text-zinc-500 text-xs truncate">{latestChart.filename}</p>
              </div>
            ) : (
              <p className="text-zinc-500 text-sm">No charts generated yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
